import React, { useState } from 'react';
import { 
  StyleSheet, 
  View, 
  Text, 
  Image, 
  TouchableOpacity 
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '../ui/Card';
import { DropdownMenu } from '../ui/DropdownMenu';
import { Vehicle } from '../../types';
import { getVehicleImageByType } from '../../utils/sampleImages';
import { useDisplaySettingsStore } from '../../stores/displaySettingsStore';
import theme from '../../theme';

interface VehicleGridCardProps {
  vehicle: Vehicle;
  onPress?: () => void;
  onViewDetails?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onAssign?: () => void;
  onUnassign?: () => void;
  onScheduleService?: () => void;
}

export const VehicleGridCard = ({ 
  vehicle, 
  onPress,
  onViewDetails,
  onEdit,
  onDelete,
  onAssign,
  onUnassign,
  onScheduleService,
}: VehicleGridCardProps) => {
  const [imageError, setImageError] = useState(false);
  
  const {
    showVehicleRentalRates,
    showVehicleImages,
  } = useDisplaySettingsStore();

  // Helper to get status color
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'available':
        return theme.colors.statusAvailable;
      case 'work':
        return theme.colors.statusWorking;
      case 'in_workshop':
        return theme.colors.statusInWorkshop;
      case 'out_of_service':
      case 'stop':
      case 'cancellation':
        return theme.colors.statusOutOfService;
      default:
        return theme.colors.gray400;
    }
  };

  // Helper to get status text
  const getStatusText = (status?: string) => {
    switch (status) {
      case 'available':
        return 'Available';
      case 'work':
        return 'Working';
      case 'in_workshop':
        return 'In Workshop';
      case 'out_of_service':
        return 'Out of Service';
      case 'stop':
        return 'Stopped';
      case 'cancellation':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };

  const handleMorePress = () => {
    // Show action sheet or modal with options
    // For now, let's just call the first action
    if (onViewDetails) onViewDetails();
  };

  // Define dropdown menu items
  const dropdownItems = [
    {
      id: 'edit',
      label: 'Edit Vehicle',
      icon: 'pencil',
      onPress: () => onEdit?.(),
    },
    // Show Assign/Unassign based on vehicle status
    vehicle.carStatus === 'available' ? {
      id: 'assign',
      label: 'Assign Driver',
      icon: 'person-add',
      onPress: () => onAssign?.(),
    } : {
      id: 'unassign',
      label: 'Unassign Driver',
      icon: 'person-remove',
      onPress: () => onUnassign?.(),
    },
    {
      id: 'delete',
      label: 'Delete Vehicle',
      icon: 'trash',
      color: theme.colors.error,
      onPress: () => onDelete?.(),
    },
  ];

  return (
    <Card
      elevation="medium"
      style={styles.card}
      contentStyle={styles.cardContent}
      onPress={onPress}
    >
      {showVehicleImages && (
        <View style={styles.imageContainer}>
          {imageError || !vehicle.image_url ? (
            <View style={styles.imagePlaceholder}>
              <Ionicons name="car-outline" size={48} color={theme.colors.gray400} />
            </View>
          ) : (
            <Image
              source={{ uri: vehicle.image_url }}
              style={styles.vehicleImage}
              resizeMode="cover"
              onError={() => {
                setImageError(true);
                console.log(`Failed to load image for vehicle ${vehicle.licensePlate}`);
              }}
            />
          )}
          
          <View style={styles.moreButton}>
            <DropdownMenu 
              items={dropdownItems}
              triggerComponent={
                <View style={styles.moreButtonInner}>
                  <Ionicons 
                    name="ellipsis-vertical" 
                    size={16} 
                    color={theme.colors.textSecondary} 
                  />
                </View>
              }
            />
          </View>
          
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(vehicle.carStatus) }]}>
            <Text style={styles.statusText}>{getStatusText(vehicle.carStatus)}</Text>
          </View>
        </View>
      )}
      
      <View style={styles.cardBody}>
        <View style={styles.titleRow}>
          <Text style={styles.makeModel} numberOfLines={1}>
            {vehicle.makeModel || 'Unknown Vehicle'}
          </Text>
        </View>
        
        <Text style={styles.details} numberOfLines={1}>
          {vehicle.year ? `${vehicle.year} • ` : ''}
          {vehicle.licensePlate || 'No Plate'} 
          {vehicle.fleetType ? ` • ${vehicle.fleetType}` : ''}
        </Text>
        
        <View style={styles.footer}>
          <Text style={styles.location} numberOfLines={1}>
            {vehicle.currentDriverName ? `Driver: ${vehicle.currentDriverName}` : 'No Driver'}
          </Text>
          
          {showVehicleRentalRates && vehicle.monthlyRentalRate ? (
            <Text style={styles.price}>
              <Text style={styles.priceValue}>AED {vehicle.monthlyRentalRate}</Text>
              <Text style={styles.priceUnit}> per month</Text>
            </Text>
          ) : null}
        </View>
      </View>
    </Card>
  );
};

const styles = StyleSheet.create({
  card: {
    flex: 1,
    margin: theme.spacing.xs,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
  },
  cardContent: {
    padding: 0,
  },
  imageContainer: {
    height: 100, // Reduced height for 5-column layout
    position: 'relative',
  },
  vehicleImage: {
    width: '100%',
    height: '100%',
  },
  imagePlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.colors.gray100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  moreButton: {
    position: 'absolute',
    top: theme.spacing.sm,
    right: theme.spacing.sm,
    zIndex: 10,
  },
  moreButtonInner: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    ...theme.shadows.sm,
  },
  statusBadge: {
    position: 'absolute',
    top: theme.spacing.sm,
    left: theme.spacing.sm,
    paddingVertical: 4,
    paddingHorizontal: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusText: {
    fontSize: theme.typography.sizes.xs,
    fontWeight: '500' as any,
    color: theme.colors.white,
  },
  cardBody: {
    padding: theme.spacing.sm, // Reduced padding for 5-column layout
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  makeModel: {
    fontSize: theme.typography.sizes.sm, // Smaller font for 5-column layout
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: 2,
  },
  details: {
    fontSize: theme.typography.sizes.xs, // Smaller font for 5-column layout
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xs, // Reduced margin
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  location: {
    fontSize: theme.typography.sizes.xs, // Smaller font for 5-column layout
    color: theme.colors.textSecondary,
    flex: 1,
    marginRight: theme.spacing.xs, // Reduced margin
  },
  price: {
    textAlign: 'right',
  },
  priceValue: {
    fontSize: theme.typography.sizes.sm, // Smaller font for 5-column layout
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  priceUnit: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
  },
});
